import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "contentHeader flex" }
const _hoisted_2 = { class: "contentBox" }
const _hoisted_3 = { class: "block w-full" }
const _hoisted_4 = { class: "flex mb-4" }
const _hoisted_5 = { class: "ml-auto" }
const _hoisted_6 = { class: "text-center" }

import {useSystemStore} from "@/stores/system/SystemStore";
import {BryntumGrid} from "@bryntum/grid-vue-3";
import GridInputFilter from "@/components/system/GridInputFilter.vue";
import {ErrorLocationType, PopupType, TableType} from "@/utilities/system/enums/Enums";
import {onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {Menu, Store, StringHelper} from "@bryntum/grid";
import {defaultSpaceAroundTableOnPage} from "@/AppConfig";
import {useI18n} from "vue-i18n";
import Error from "@/components/system/Error.vue";
import LoadingSpinner from "@/components/system/LoadingSpinner.vue";
import {SettingsService} from "@/utilities/services/entities/SettingsService";
import {GlobalSetting} from "@/models/entities/settings/GlobalSetting";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import DateToStringHelper from "@/utilities/system/helpers/DateToStringHelper";



export default /*#__PURE__*/_defineComponent({
  __name: 'SettingsManagment',
  setup(__props) {

const {t} = useI18n();
const systemStore = useSystemStore();
const settingsService = new SettingsService();

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
  currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage}px`;
};

const gridStore = new Store()
const grid = ref(null)
const useGridConfig = {
  headerMenuFeature: false,
  relayStoreEvents: true,
  cellTooltipFeature: {
    hoverDelay      : 300,
    textContent     : true,
    hideOnDelegateChange: true,
  },
  enableTextSelection: false,
  selectionMode : {},
  cellEditFeature: false,
  filterBarFeature: false,
  rowReorderFeature: true,
  stripeFeature: false,
  treeFeature: false,
  columns : {
    data: [
      {
        id:'displayName',
        field: 'displayName',
        text: t('settingManagment.grid.displayName'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id: 'description',
        field: 'description',
        text: "",
        width: 50,
        sortable: false,
        resizable: false,
        minWidth: 50,
        type: "template",
        tooltipRenderer : ({record}:any) => `${record.data.description}`,
        template: ({record}:any) => {
          if(record.data.description !== null)
            return `<div class='gridTooltipButton'><span class='icon-info'></span></div>`;
          return ""
        },
      },
      {
        id:'value',
        field: 'value',
        text: t('settingManagment.grid.value'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          return StringHelper.xss`${value ?? ""}`;
        }
      },
      {
        id:'editDate',
        field: 'editDate',
        text: t('settingManagment.grid.editDate'),
        flex: 1,
        type: "template",
        template: ({value}:any) => {
          if(NullOrUndefinedHelper(value))return "";

          const date = new Date(`${value}Z`);
          return DateToStringHelper(date);
        },
      },
      {
        id:'menu',
        text	: '',
        width   : 36,
        type	: 'widget',
        align: "center",
        hideable: false,
        resizable: false,
        sortable: false,
        filterable: false,
        widgets : [{
          type	 : 'button',
          width: 36,
          icon	 : 'icon-kontext_horizontal',
          onAction : ({ source: btn }:any) => {
            const menu = menuFactory(btn.cellInfo.record.data);
            menu.alignTo({ target: btn })
            menu.show();
          }
        }]
      },
    ],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await systemStore.saveUserColumnOrder(TableType[TableType.settings], event.records[0].id, event.index.toString());
        }
      },
    }
  },
  store: gridStore,
  listeners: {
    datachange(){
      systemStore.currentItems = gridStore.count;
      systemStore.allItems = gridStore.getCount();
    },
  },
  height: currentWindowHeight,
}
const gridConfig = reactive(useGridConfig)

const menuFactory = (record:object) => {
  const menu = new Menu({
    anchor   : true,
    autoShow : true,
    items	: [
      {
        text: t('global.edit'),
        cls: 'color',
        icon: 'icon-editieren',
        weight: 100,
        onItem : ({menu}:any) => {
          systemStore.openPopup({
            data: menu.record,
            action: editGlobalSetting,
            resetButton:false,
            title:t("settingManagment.popups.editGlobal.title"),
            type:PopupType.editGlobalSettingsPopup
          });
        }
      },
    ],
  });

  menu.setRecord(record);

  return menu;
};


const editGlobalSetting = async ()=>{
  const setting = systemStore.modal.contentObject as GlobalSetting;

  const updateResp = await settingsService.editGlobalSetting(setting, ErrorLocationType.popup)
  if(!updateResp) return

  // remove Z from ISO string in end since it cannot be casted to 'Date' when forwarded to the DateStringHelper
  setting.editDate = (new Date()).toISOString().slice(0, -1);

  gridStore.add(setting);

  systemStore.closeModal();
  systemStore.showSuccess(t("global.saved"));
}

const setGridData = async () => {
  const globalSettings = await settingsService.getGlobalSettings(ErrorLocationType.table);
  if (!globalSettings) return;

  gridStore.setStoreData(globalSettings);

  systemStore.reorderColumns(TableType[TableType.settings]);
}

onMounted(async ()=>{
  // calculate grid height and sub to window resize event
  recalcGridHeight();
  window.addEventListener('resize', recalcGridHeight);

  systemStore.setGridStore(ref(gridStore));
  systemStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

  // start spinner
  systemStore.gridDataIsLoading = true;
  await setGridData();
  // stop spinner
  systemStore.gridDataIsLoading = false;
})

onBeforeUnmount(async ()=>{
  window.removeEventListener('resize', recalcGridHeight);

  systemStore.setGridStore(null);
  systemStore.setGridColumnStore(null);

  systemStore.currentItems = 0;
  systemStore.allItems = 0;
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h4", null, _toDisplayString(_ctx.$t("settingManagment.title")), 1)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _withDirectives(_createElementVNode("div", null, [
        _createVNode(Error, {
          errorLoc: _unref(ErrorLocationType).table
        }, null, 8, ["errorLoc"]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(GridInputFilter, {
                gridStore: _unref(gridStore),
                hideFilterButton: true
              }, null, 8, ["gridStore"])
            ])
          ]),
          _createVNode(_unref(BryntumGrid), _mergeProps({
            ref_key: "grid",
            ref: grid
          }, gridConfig, { readOnly: true }), null, 16)
        ])
      ], 512), [
        [_vShow, !_unref(systemStore).gridDataIsLoading]
      ]),
      _withDirectives(_createElementVNode("div", _hoisted_6, [
        _createVNode(LoadingSpinner)
      ], 512), [
        [_vShow, _unref(systemStore).gridDataIsLoading]
      ])
    ])
  ], 64))
}
}

})